import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'
import type { FertilizerProduct } from './types'

const BROWSE_LIMIT = 50
const SEARCH_LIMIT = 100

export const CATEGORY_LABELS: Record<FertilizerProduct['category'], string> = {
  COMPLETE_NPK: 'Complete NPK',
  PARTIAL_NPK: 'Two of N, P, K',
  STRAIGHT_NITROGEN: 'Nitrogen',
  STRAIGHT_PHOSPHORUS: 'Phosphorus',
  STRAIGHT_POTASSIUM: 'Potassium',
  SECONDARY: 'Calcium, magnesium, sulphur',
  MICRONUTRIENT: 'Micronutrient',
  STARTER: 'Starter',
  BIOSTIMULANT: 'Biostimulant',
  HUMIC: 'Humic',
  OTHER: 'Other',
}

/** Nutrients in the order a label lists them (N, P, K first), with the form each is stated in. */
export const NUTRIENT_ORDER = ['N', 'P2O5', 'K2O', 'CA', 'MG', 'S', 'B', 'CU', 'FE', 'MN', 'MO', 'ZN', 'SI'] as const
export const NUTRIENT_LABELS: Record<string, string> = {
  N: 'Nitrogen (N)',
  P2O5: 'Phosphate (P₂O₅)',
  K2O: 'Potash (K₂O)',
  CA: 'Calcium (Ca)',
  MG: 'Magnesium (Mg)',
  S: 'Sulphur (S)',
  B: 'Boron (B)',
  CU: 'Copper (Cu)',
  FE: 'Iron (Fe)',
  MN: 'Manganese (Mn)',
  MO: 'Molybdenum (Mo)',
  ZN: 'Zinc (Zn)',
  SI: 'Silicon (Si)',
}

export const FORM_LABELS: Record<FertilizerProduct['forms'][number], string> = { SOLID: 'Solid', LIQUID: 'Liquid', GAS: 'Gas' }
export const MODE_LABELS: Record<FertilizerProduct['applicationModes'][number], string> = {
  SOIL: 'Soil',
  FOLIAR: 'Foliar',
  FERTIGATION: 'Fertigation',
  SEED: 'Seed treatment',
  DRY_BLEND_COATING: 'Coating for dry blends',
}
export const SEGMENT_LABELS: Record<string, string> = { BLUEBERRY: 'Blueberries', FIELD_CROPS: 'Field crops', GREENHOUSE: 'Greenhouse', NURSERY: 'Nursery' }
export const CERTIFIER_LABELS: Record<string, string> = { ECOCERT: 'Ecocert', OMRI: 'OMRI', PRO_CERT: 'Pro-Cert' }

/** One-shot search by name, brand or supplier, for the command palette (no hook needed there). */
export async function searchFertilizers(q: string, limit = 25): Promise<FertilizerProduct[]> {
  if (!q.trim()) return []
  return api.get<FertilizerProduct[]>(`/fertilizers?q=${encodeURIComponent(q.trim())}&limit=${limit}`)
}

/** One-shot fetch for the detail page. */
export const getFertilizer = (id: string) => api.get<FertilizerProduct>(`/fertilizers/${id}`)

/** One product for the detail page. `id` undefined loads nothing. */
export function useFertilizer(id: string | undefined) {
  const [fertilizer, setFertilizer] = useState<FertilizerProduct | null>(null)
  const [loading, setLoading] = useState(!!id)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setFertilizer(null)
    setError(null)
    if (!id) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    getFertilizer(id)
      .then((data) => !cancelled && setFertilizer(data))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Could not load this product.'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [id])

  return { fertilizer, loading, error }
}

/**
 * Backs the /fertilizers page: `query` empty browses alphabetically (cursor-paginated, "load more");
 * non-empty switches to a server-side search (a capped list). A request-generation guard means a
 * slow, stale response can never overwrite a newer one's results.
 */
export function useFertilizers(query: string) {
  const [items, setItems] = useState<FertilizerProduct[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const requestId = useRef(0)

  const load = useCallback(async (q: string) => {
    const id = ++requestId.current
    setLoading(true)
    setError(null)
    try {
      if (q.trim()) {
        const results = await api.get<FertilizerProduct[]>(`/fertilizers?q=${encodeURIComponent(q.trim())}&limit=${SEARCH_LIMIT}`)
        if (id !== requestId.current) return
        setItems(results)
        setNextCursor(null)
      } else {
        const page = await api.getPage<FertilizerProduct>(`/fertilizers?limit=${BROWSE_LIMIT}`)
        if (id !== requestId.current) return
        setItems(page.data)
        setNextCursor(page.nextCursor)
      }
    } catch (err) {
      if (id !== requestId.current) return
      setError(err instanceof Error ? err.message : 'Could not load fertilizers.')
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(query)
  }, [query, load])

  async function loadMore() {
    if (!nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const page = await api.getPage<FertilizerProduct>(`/fertilizers?cursor=${encodeURIComponent(nextCursor)}&limit=${BROWSE_LIMIT}`)
      setItems((prev) => [...prev, ...page.data])
      setNextCursor(page.nextCursor)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load more fertilizers.')
    } finally {
      setLoadingMore(false)
    }
  }

  return { items, loading, loadingMore, error, hasMore: nextCursor !== null, loadMore }
}
